import { useNavigate, useLocation } from "react-router-dom";
import { Dices } from "lucide-react";
import { useMusicStore } from "~/stores/useMusicStore";
import { useToastStore } from "~/stores/useToastStore";
import Button from "~/components/ui/Button";
import Switch from "~/components/ui/Switch";
import Collapsible from "~/components/ui/Collapsible";
import ScrollArea from "~/components/ui/ScrollArea";
import DeviceProgressBar from "~/components/DeviceProgressBar";

export default function LeftPanel() {
  const { devices, toggleDevice, toggleDevicePanel, deviceProgressPercentages, deviceTransitionDurations, deviceCurrentNotes, devicePatternPositions, scrambleDeviceCCs, scrambleDevicePattern, noteGenerators } = useMusicStore();
  const { addToast } = useToastStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleRandomizeCCs = async (deviceIndex: number) => {
    console.log(`🎲 [RANDOMIZE CCs] Device index: ${deviceIndex}`);
    await scrambleDeviceCCs(deviceIndex);
    // Show toast notification
    addToast('CCs scrambled', 'success');
  };

  const handleRandomizePattern = (deviceIndex: number) => {
    console.log(`🎲 [RANDOMIZE PATTERN] Device index: ${deviceIndex}`);
    scrambleDevicePattern(deviceIndex);
    // Show toast notification
    addToast('Pattern scrambled', 'success');
  };

  return (
    <div className="flex-none w-40 sm:w-[200px] h-full border-r-4 border-gray-900 bg-gray-950">
      {/* Scrollable Devices List */}
      <ScrollArea className="h-full">
        {/* Show prompt when no devices are expanded */}
        {devices.length === 0 && (
          <div className="p-4 text-gray-400 text-sm text-center">
            No devices detected
          </div>
        )}

        {/* Device Collapsibles */}
        {devices.map((device, index) => {
          const isDisconnected = device.state === "disconnected";
          const flashState = device.flashState || "none";
          const patternSequence = device.pattern?.pattern?.sequence || [];
          const currentPartIndex = devicePatternPositions[index] || 0;

          // Format scale info: rootNote + scale abbreviation (which includes mode)
          const rootNote = device.pattern?.rootNote?.note || "—";
          const scaleAbbr = device.pattern?.scale?.abbr || "—";
          const scaleInfo = `${rootNote} ${scaleAbbr}`;

          // Check if current route is active for this device
          const isPatternActive = location.pathname === `/pattern/${index}`;
          const isCCsActive = location.pathname === `/control-changes/${index}`;

          return (
            <Collapsible
              key={device.uniqueId || device.label}
              open={device.panelOpen ?? true}
              onOpenChange={(open) => toggleDevicePanel(index, open)}
              className={`border-b border-gray-700 ${
                isDisconnected ? "opacity-50" : ""
              } ${
                flashState === "green" ? "bg-green-500/20" : ""
              } ${
                flashState === "red" ? "bg-red-500/20" : ""
              }`}
            >
              <Collapsible.Trigger>
                <div className="flex items-center gap-2 w-full">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    isDisconnected ? "bg-gray-600" :
                    device.active ? "bg-green-500" :
                    "bg-red-500"
                  }`} />
                  <span className="truncate">{device.label}</span>
                </div>
              </Collapsible.Trigger>
              <DeviceProgressBar
                deviceIndex={index}
                progress={deviceProgressPercentages[index] || 0}
                disabled={isDisconnected}
                muted={!device.active}
                transitionDurationMs={deviceTransitionDurations[index] || 250}
              />

              <Collapsible.Content className="p-4 bg-gray-900 space-y-3">

                {/* Toggle Switch and Note Indicator */}
                <div className="flex items-center justify-between gap-2">
                  <Switch
                    checked={device.active}
                    onCheckedChange={() => toggleDevice(device.label)}
                    disabled={isDisconnected}
                    aria-label={`${device.active ? "Turn off" : "Turn on"} ${device.label}`}
                  />
                  <div className="flex items-center gap-1 text-xs">
                    {noteGenerators[index]?.running ? (
                      deviceCurrentNotes[index] ? (
                        <span className="font-mono text-sm text-purple-300">
                          {deviceCurrentNotes[index]}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-base" aria-hidden="true">♪</span>
                      )
                    ) : (
                      <span className="text-gray-500 text-base" aria-hidden="true">♪</span>
                    )}
                  </div>
                </div>

                {/* Pattern Name and Scale Info */}
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-xs font-bold flex gap-0.5">
                    {patternSequence.map((part, partIndex) => (
                      <span
                        key={partIndex}
                        className={partIndex === currentPartIndex ? "text-gray-200 underline" : "text-gray-400"}
                      >
                        {part}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-gray-400">
                    {scaleInfo}
                  </div>
                </div>

                {/* Navigation & Scramble Buttons */}
                <div className="space-y-2">
                  {/* Scramble Pattern Row */}
                  <div className="flex gap-1">
                    <Button
                      variant="icon"
                      size="sm"
                      onClick={() => handleRandomizePattern(index)}
                      disabled={isDisconnected}
                      className="flex-shrink-0"
                      aria-label="Scramble pattern"
                    >
                      <Dices className="w-6 h-6" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/pattern/${index}`)}
                      disabled={isDisconnected}
                      className={`text-sm flex-1 justify-start ${isPatternActive ? "border-green-500" : ""}`}
                    >
                      Pattern
                    </Button>
                  </div>

                  {/* Scramble CCs Row */}
                  <div className="flex gap-1">
                    <Button
                      variant="icon"
                      size="sm"
                      onClick={() => handleRandomizeCCs(index)}
                      disabled={isDisconnected}
                      className="flex-shrink-0"
                      aria-label="Scramble control changes"
                    >
                      <Dices className="w-6 h-6" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/control-changes/${index}`)}
                      disabled={isDisconnected}
                      className={`text-sm flex-1 justify-start ${isCCsActive ? "border-green-500" : ""}`}
                    >
                      CCs
                    </Button>
                  </div>
                </div>
              </Collapsible.Content>
            </Collapsible>
          );
        })}
      </ScrollArea>
    </div>
  );
}
